import { useCallback } from 'react'
import styled from 'styled-components'

import { useDialog } from '@loadsmart/loadsmart-ui'
import { Layout, Text, Card, Tag, Button } from '@loadsmart/miranda-react'

import { type StopAppointment } from 'core/appointments/domain/Appointment'
import {
  formatAppointmentStatus,
  formatAppointmentDescription,
  formatAppointmentStatusColor,
} from 'core/appointments/presenters/appointmentPresenter'
import { Icon } from 'core/ui/components/icon/Icon'

import openDockLogo from '../../assets/opendock-logo.png'
import { getAppointmentSlots } from '../../domain/Appointment'
import { type Contact, type Facility } from '../../domain/Facility'
import { type Carrier } from '../../domain/Shipment'
import {
  formatMisschedulingReason,
  formatScheduleDate,
  formatStatusTagColor,
  formatActionButtonVariant,
  formatActionLabel,
  isActionButtonDisabled,
} from '../../presenters/appointmentPresenter'
import { AppointmentCardEmptyState } from './AppointmentCardEmptyState'
import { AppointmentDialogs } from './AppointmentDialogs'
import { MissingDate } from './MissingDate'
import { SchedulingStatusTag } from './SchedulingStatusTag'

export type AppointmentCardProps = {
  shipmentUUID: string
  shipmentCarrier: Carrier
  stopIndex: number
  stopDate?: string
  appointment?: StopAppointment
  facilityName?: string
  facilityContact?: Partial<Contact>
  warehouseUUID?: string | null
  onConfirm: (uuid: string) => Promise<void>
  facility?: Partial<Facility>
}

const CapitalizedText = styled(Text)`
  text-transform: capitalize;
`
const UppercasedText = styled(Text)`
  text-transform: uppercase;
`

export function AppointmentCard({
  shipmentUUID,
  shipmentCarrier,
  stopIndex,
  stopDate,
  appointment,
  facility,
  facilityContact,
  onConfirm,
}: AppointmentCardProps) {
  const showOpenDockAppointmentDrawer = Boolean(facility?.warehouseUuid)

  const { open, show, hide } = useDialog({ open: false })

  const isOpenDockFacility = Boolean(facility?.warehouseUuid)
  const appointmentDialogIsOpen = Boolean(open && !facility?.warehouseUuid)
  const openDockAppointmentIsOpen = Boolean(open && facility?.warehouseUuid)

  const handleConfirm = useCallback(() => {
    onConfirm(shipmentUUID)
    hide()
  }, [hide, onConfirm, shipmentUUID])

  if (!appointment) {
    return (
      <>
        <AppointmentCardEmptyState
          onButtonClick={show}
          isOpenDockFacility={isOpenDockFacility}
          disabled={!facility}
        />
        <AppointmentDialogs
          showOpenDockAppointmentDrawer={showOpenDockAppointmentDrawer}
          facilityContact={facilityContact}
          shipmentUUID={shipmentUUID}
          stopIndex={stopIndex}
          stopDate={stopDate ?? ''}
          appointment={appointment}
          appointmentDialogIsOpen={appointmentDialogIsOpen}
          openDockAppointmentIsOpen={openDockAppointmentIsOpen}
          facility={facility}
          onClose={hide}
          onConfirm={handleConfirm}
          shipmentCarrier={shipmentCarrier}
        />
      </>
    )
  }

  const { date: appointmentDate, time: appointmentTime } = getAppointmentSlots(appointment)
  const displayFooter = appointment.scheduledBy || appointment.confirmationNumber
  const cardStatusColor = formatAppointmentStatusColor(appointment.status)
  const tagStatusVariant = formatStatusTagColor(appointment.status)

  function handleOpenAppointment() {
    show()
  }

  return (
    <>
      <Layout.Stack>
        <Text variant="heading-sm-bold">Schedule appointment</Text>
        <Card flagged flag-color={cardStatusColor}>
          <Card.Body>
            <Layout.Group justify="space-between" align="center">
              <Layout.Group align="center">
                <Icon name="clock" width={22} height={22} />
                <Layout.Stack gap="spacing-1">
                  <Layout.Group gap="spacing-1">
                    <Tag variant={tagStatusVariant} size="small">
                      {formatAppointmentStatus(appointment.status)}
                    </Tag>
                    <SchedulingStatusTag appointment={appointment} />
                  </Layout.Group>
                  {appointmentDate && appointmentTime ? (
                    <>
                      <Text variant="body-md-bold" color="color-text-tertiary">
                        {formatScheduleDate(appointmentDate)}
                      </Text>
                      <div>
                        <Text variant="heading-md" color="color-text-primary">
                          {formatAppointmentDescription(appointment)}{' '}
                        </Text>
                        <UppercasedText variant="heading-md" color="color-text-primary">
                          {`(${appointment.mode} -`}
                        </UppercasedText>
                        <CapitalizedText variant="heading-md" color="color-text-primary">
                          {`${appointment.loadingType})`}
                        </CapitalizedText>
                      </div>
                    </>
                  ) : (
                    <MissingDate />
                  )}
                  <Layout.Stack gap="spacing-0-5">
                    {appointment.misschedulingReason && (
                      <Text variant="body-sm" color="color-warning-100">
                        <CapitalizedText color="color-warning-100" variant="body-sm">
                          Reason: {formatMisschedulingReason(appointment.misschedulingReason)}
                        </CapitalizedText>{' '}
                        {appointment.misschedulingNote && `- ${appointment.misschedulingNote}`}
                      </Text>
                    )}
                    {displayFooter && (
                      <Text variant="body-sm" color="color-text-tertiary">
                        {appointment.scheduledBy && `Scheduled by ${appointment.scheduledBy} `}
                        {appointment.confirmationNumber &&
                          `- Confirmation number ${appointment.confirmationNumber}`}
                      </Text>
                    )}
                  </Layout.Stack>
                </Layout.Stack>
              </Layout.Group>
              <Layout.Stack align="flex-end">
                {isOpenDockFacility && <img src={openDockLogo} alt="OpenDock" width={105} />}
                <Button
                  type="button"
                  variant={formatActionButtonVariant(appointment.status)}
                  size="small"
                  disabled={isActionButtonDisabled(appointment.status)}
                  onClick={handleOpenAppointment}
                >
                  {formatActionLabel(appointment.status)}
                </Button>
              </Layout.Stack>
            </Layout.Group>
          </Card.Body>
        </Card>
      </Layout.Stack>

      <AppointmentDialogs
        showOpenDockAppointmentDrawer={showOpenDockAppointmentDrawer}
        facilityContact={facilityContact ?? {}}
        shipmentUUID={shipmentUUID}
        stopIndex={stopIndex}
        stopDate={stopDate ?? ''}
        appointment={appointment}
        appointmentDialogIsOpen={appointmentDialogIsOpen}
        openDockAppointmentIsOpen={openDockAppointmentIsOpen}
        facility={facility}
        onClose={hide}
        onConfirm={handleConfirm}
        shipmentCarrier={shipmentCarrier}
      />
    </>
  )
}
